import React, { useState, useEffect, useCallback, useRef } from 'react';
import moment from 'moment';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useToast } from '../../context/ToastContext';
import { FiSearch, FiRefreshCw, FiX, FiCheck, FiChevronLeft, FiChevronRight, FiThumbsUp, FiThumbsDown, FiMessageSquare } from 'react-icons/fi';
import PreviewIcon from '../../assets/images/ViewDetailsIcon.svg';
import { Skeleton } from 'primereact/skeleton';
import '../commissionscan/CommissionScan.css';
import '../../styles/Voice.css';
import '../../styles/QuoteProposal.css';

const getToken    = () => localStorage.getItem('toolboxToken') || '';
const getNcToken  = () => localStorage.getItem('amsToken')     || '';

/* ── Status pill ── */
const STATUS_CONFIG: Record<number, { label: string; color: string }> = {
  0: { label: 'Submitted',     color: '#fbbf24' },
  1: { label: 'In Progress',   color: '#a78bfa' },
  2: { label: 'Done',          color: '#34d399' },
  3: { label: 'Failed',        color: '#f87171' },
  4: { label: 'Not Supported', color: '#9ca3af' },
};

interface HistoryRow {
  transactionId: string;
  fileName: string;
  userName: string;
  status: number;
  created: string;
  llmResponse: string | null;
  isUpdateInAMS: boolean;
  insuredName?: string;
  totalCount: number;
  isLiked?: boolean | null;
}

interface ClaimRow {
  id: string;
  ClaimNumber?: string;
  Year?: string;
  Carrier?: string;
  PolicyNumber?: string;
  LOB?: string;
  Premium?: string;
  PolicyStart?: string;
  PolicyEnd?: string;
  LossDate?: string;
  Incurred?: string;
  Paid?: string;
  Outstanding?: string;
  Description?: string;
  AddressLine1?: string;
  City?: string;
  State?: string;
  ZipCode?: string;
}

interface Client { name: string; databaseId: string; }

const PAGE_SIZE = 10;

const LossRunScanHistory: React.FC = () => {
  const [historyData,    setHistoryData]    = useState<HistoryRow[]>([]);
  const [loading,        setLoading]        = useState(true);
  const [globalFilter,   setGlobalFilter]   = useState('');
  const [page,           setPage]           = useState(1);
  const [totalRecords,   setTotalRecords]   = useState(0);

  /* Preview dialog */
  const [showPopup,         setShowPopup]         = useState(false);
  const [previewLoading,    setPreviewLoading]    = useState(false);
  const [pdfUrl,            setPdfUrl]            = useState('');
  const [isFilePdf,         setIsFilePdf]         = useState(false);
  const [llmResponse,       setLlmResponse]       = useState<ClaimRow[]>([]);
  const [selectedRows,      setSelectedRows]      = useState<ClaimRow[]>([]);
  const [activeRow,         setActiveRow]         = useState<HistoryRow | null>(null);
  const [isUpdateInAMS,     setIsUpdateInAMS]     = useState(false);
  const [transactionId,     setTransactionId]     = useState('');
  const [fileName,          setFileName]          = useState('');
  const [claimsPage,        setClaimsPage]        = useState(1);

  /* Client search inside preview */
  const [insuredClientName, setInsuredClientName] = useState('');
  const [insuredSearch,     setInsuredSearch]     = useState('');
  const [clientResults,     setClientResults]     = useState<Client[]>([]);
  const [clientLoading,     setClientLoading]     = useState(false);
  const [clientId,          setClientId]          = useState('');
  const [clientName,        setClientName]        = useState('');
  const [clientNameError,   setClientNameError]   = useState('');

  /* Insert claim */
  const [insertLoading,     setInsertLoading]     = useState(false);
  const [claimInserted,     setClaimInserted]     = useState(false);

  /* Upload to AMS dialog */
  const [showUploadDialog,       setShowUploadDialog]       = useState(false);
  const [uploadClientSearch,     setUploadClientSearch]     = useState('');
  const [uploadClientName,       setUploadClientName]       = useState('');
  const [uploadClientList,       setUploadClientList]       = useState<Client[]>([]);
  const [uploadClientLoading,    setUploadClientLoading]    = useState(false);
  const [uploadInsuredId,        setUploadInsuredId]        = useState('');
  const [uploadToAmsLoading,     setUploadToAmsLoading]     = useState(false);

  /* Toast */
  const { showSuccess, showError } = useToast();

  /* Feedback */
  const [feedbackTxId,       setFeedbackTxId]       = useState<string | null>(null);
  const [feedbackText,       setFeedbackText]       = useState('');
  const [feedbackRating,     setFeedbackRating]     = useState<boolean | null>(null);
  const [feedbackAnchor,     setFeedbackAnchor]     = useState<{ top: number; right: number } | null>(null);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedBackError,      setFeedBackError]      = useState('');
  const feedbackRef = useRef<HTMLDivElement>(null);

  /* ── Feedback handlers ── */
  const openFeedback = useCallback((row: HistoryRow, e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setFeedbackTxId(row.transactionId);
    setFeedbackText('');
    setFeedbackRating(row.isLiked ?? null);
    setFeedBackError('');
    setFeedbackAnchor({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
  }, []);

  const closeFeedback = useCallback(() => {
    setFeedbackTxId(null);
    setFeedbackAnchor(null);
    setFeedbackText('');
    setFeedbackRating(null);
    setFeedBackError('');
  }, []);

  const saveFeedbackRating = useCallback((isLiked: boolean | null) => {
    if (!feedbackTxId) return;
    axios.post(`${(process.env.REACT_APP_DataFabric_Url       || '')}/api/NCAICommissionStatement/feedback`,
      { Target: 7, TransactionId: feedbackTxId, IsLiked: isLiked, Feedback: feedbackText ?? '' },
      { headers: { Authorization: `Bearer ${getToken()}` } },
    ).then(() => { showSuccess('Feedback submitted successfully!'); closeFeedback(); }).catch(() => {});
  }, [feedbackTxId, feedbackText, closeFeedback, showSuccess]);

  const onThumbsUpClick = useCallback(() => {
    if (!feedbackTxId) return;
    const currentRow = historyData.find(r => r.transactionId === feedbackTxId);
    const newLiked = currentRow?.isLiked === true ? null : true;
    setHistoryData(prev => prev.map(r => r.transactionId === feedbackTxId ? { ...r, isLiked: newLiked } : r));
    closeFeedback();
    saveFeedbackRating(newLiked);
  }, [feedbackTxId, historyData, saveFeedbackRating, closeFeedback]);

  const onThumbsDownClick = useCallback(() => {
    if (!feedbackTxId) return;
    const currentRow = historyData.find(r => r.transactionId === feedbackTxId);
    const newLiked = currentRow?.isLiked === false ? null : false;
    setHistoryData(prev => prev.map(r => r.transactionId === feedbackTxId ? { ...r, isLiked: newLiked } : r));
    closeFeedback();
    saveFeedbackRating(newLiked);
  }, [feedbackTxId, historyData, saveFeedbackRating, closeFeedback]);

  const onSubmitFeedbackClick = useCallback(() => {
    if (!feedbackTxId) return;
    if (!feedbackText.trim()) { setFeedBackError('Please enter your feedback'); return; }
    if (feedbackText.length > 250) { setFeedBackError('Please provide your feedback within 250 characters'); return; }
    setFeedBackError('');
    setFeedbackSubmitting(true);
    axios.post(`${(process.env.REACT_APP_DataFabric_Url       || '')}/api/NCAICommissionStatement/feedback`,
      { Target: 7, TransactionId: feedbackTxId, IsLiked: feedbackRating, Feedback: feedbackText },
      { headers: { Authorization: `Bearer ${getToken()}` } },
    )
      .then(() => { setFeedbackSubmitting(false); showSuccess('Feedback submitted successfully!'); closeFeedback(); })
      .catch(() => { setFeedbackSubmitting(false); });
  }, [feedbackTxId, feedbackText, feedbackRating, closeFeedback, showSuccess]);

  useEffect(() => {
    if (!feedbackTxId) return;
    const handler = (e: MouseEvent) => {
      if (feedbackRef.current && !feedbackRef.current.contains(e.target as Node)) closeFeedback();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [feedbackTxId, closeFeedback]);

  /* ── Fetch history ── */
  const fetchHistory = useCallback(() => {
    setLoading(true);
    axios.get(`${(process.env.REACT_APP_DataFabric_Url       || '')}/api/LossRunScan/history`, {
      params: { pageNumber: page, recordCount: PAGE_SIZE },
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then(res => {
        setHistoryData(res.data || []);
        if (res.data?.length > 0) setTotalRecords(res.data[0].totalCount);
        else setTotalRecords(0);
      })
      .catch(() => showError('Error loading history.'))
      .finally(() => setLoading(false));
  }, [page, showError]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  /* ── Client search for insert dialog ── */
  useEffect(() => {
    if (!insuredSearch.trim()) { setClientResults([]); return; }
    const t = setTimeout(() => {
      setClientLoading(true);
      axios.get(`${(process.env.REACT_APP_NOWCERTSAPI          || '')}/api/nowcert/customerlist`, {
        params: { searchText: insuredSearch, nowCertsToken: getNcToken() },
        headers: { Authorization: `Bearer ${getToken()}` },
      })
        .then(r => setClientResults(r.data || []))
        .finally(() => setClientLoading(false));
    }, 400);
    return () => clearTimeout(t);
  }, [insuredSearch]);

  /* ── Client search for AMS upload dialog ── */
  useEffect(() => {
    if (!uploadClientSearch.trim()) { setUploadClientList([]); return; }
    const t = setTimeout(() => {
      setUploadClientLoading(true);
      axios.get(`${(process.env.REACT_APP_NOWCERTSAPI          || '')}/api/nowcert/customerlist`, {
        params: { searchText: uploadClientSearch, nowCertsToken: getNcToken() },
        headers: { Authorization: `Bearer ${getToken()}` },
      })
        .then(r => setUploadClientList(r.data || []))
        .finally(() => setUploadClientLoading(false));
    }, 500);
    return () => clearTimeout(t);
  }, [uploadClientSearch]);

  /* ── Preview ── */
  const onPreviewClick = useCallback((row: HistoryRow) => {
    const parsed: any[] = row.llmResponse ? JSON.parse(row.llmResponse) : [];
    const withIds: ClaimRow[] = parsed.map((item, idx) => ({ ...item, id: String(idx) }));
    setLlmResponse(withIds);
    setSelectedRows([]);
    setClaimsPage(1);
    setActiveRow(row);
    setTransactionId(row.transactionId);
    setFileName(row.fileName);
    setIsUpdateInAMS(row.isUpdateInAMS);
    setInsuredClientName(row.insuredName || '');
    setClientName(row.insuredName || '');
    setClientId('');
    setInsuredSearch('');
    setClientNameError('');
    setClaimInserted(false);
    setShowPopup(true);
    setPreviewLoading(true);
    axios.post(
      `${(process.env.REACT_APP_DataFabric_Url       || '')}/api/LossRunScan/previewdocument`,
      { transactionId: row.transactionId, fileName: row.fileName },
      { headers: { Authorization: `Bearer ${getToken()}` } },
    )
      .then(res => {
        setPdfUrl(res.data.fileUrl || '');
        setIsFilePdf((res.data.fileUrl || '').toLowerCase().includes('.pdf'));
      })
      .catch(() => showError('Error loading document preview.'))
      .finally(() => setPreviewLoading(false));
  }, [showError]);

  /* ── Insert Claims into NowCerts ── */
  const insertClaimData = useCallback(async () => {
    if (!selectedRows.length) {
      showError('Please select at least one claim to insert.');
      return;
    }
    if (!insuredClientName.trim()) {
      setClientNameError('Client name is required.');
      return;
    }
    setInsertLoading(true);
    const payload = selectedRows.map(item => ({
      insured_database_id:    clientId,
      insured_commercial_name: clientName,
      status:                 '3',
      claim_number:           item.ClaimNumber && item.ClaimNumber !== 'NA' ? item.ClaimNumber : 'TBD',
      date_of_loss:           item.LossDate    && item.LossDate    !== 'NA' ? item.LossDate    : '',
      date_amount: [{
        date:   item.LossDate && item.LossDate !== 'NA' ? item.LossDate : '',
        amount: item.Incurred && item.Incurred !== 'NA' ? parseInt(item.Incurred) : 0,
      }],
      policy_number:       item.PolicyNumber && item.PolicyNumber !== 'NA' ? item.PolicyNumber : '',
      description_of_loss: item.Description  && item.Description  !== 'NA' ? item.Description  : '',
      street:              item.AddressLine1 && item.AddressLine1 !== 'NA' ? item.AddressLine1 : '',
      city:                item.City         && item.City         !== 'NA' ? item.City         : '',
      state:               item.State        && item.State        !== 'NA' ? item.State        : '',
      zip:                 item.ZipCode      && item.ZipCode      !== 'NA' ? item.ZipCode      : '',
    }));

    try {
      await axios.post(
        `${(process.env.REACT_APP_NOWCERTSAPI          || '')}/api/nowcert/insertclaim?ncToken=${getNcToken()}`,
        payload,
        { headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' } },
      );
      setClaimInserted(true);
    } catch {
      showSuccess('Error while inserting claim data.');
    } finally {
      setInsertLoading(false);
    }
  }, [selectedRows, clientId, clientName, insuredClientName, showSuccess]);

  /* ── Upload PDF to AMS ── */
  const onUploadToAMS = useCallback(async () => {
    if (!uploadInsuredId) return;
    setUploadToAmsLoading(true);
    try {
      const res = await axios.post(
        `${(process.env.REACT_APP_NOWCERTSAPI          || '')}/api/nowcert/uploadV1`,
        JSON.stringify({
          TransactionId: transactionId,
          FileName:      fileName,
          NCPolicyId:    '',
          NCInsuredId:   uploadInsuredId,
          FileData:      '',
          FolderName:    'LossRunScan',
          TargetName:    'LossRunScan',
          NowCertsToken: getNcToken(),
        }),
        { headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' } },
      );
      if (res.data.success) {
        showSuccess('PDF uploaded to AMS successfully.');
      } else {
        showError(res.data.errorMessage || 'Error uploading to AMS.');
      }
      setShowUploadDialog(false);
    } catch {
      showError('Error uploading PDF to AMS.');
    } finally {
      setUploadToAmsLoading(false);
    }
  }, [uploadInsuredId, transactionId, fileName, showSuccess, showError]);

  /* ── Filtered data ── */
  const filteredData = historyData.filter(row => {
    if (!globalFilter.trim()) return true;
    const s = globalFilter.toLowerCase();
    return (
      row.fileName?.toLowerCase().includes(s) ||
      row.userName?.toLowerCase().includes(s)
    );
  });

  /* ── Pagination helpers ── */
  const totalPages  = Math.max(1, Math.ceil(totalRecords / PAGE_SIZE));
  const claimsTotal = llmResponse.length;
  const claimsPages = Math.max(1, Math.ceil(claimsTotal / 5));
  const claimsSlice = llmResponse.slice((claimsPage - 1) * 5, claimsPage * 5);

  const isAllSelected = llmResponse.length > 0 && selectedRows.length === llmResponse.length;

  const toggleRow = (row: ClaimRow) => {
    if (isUpdateInAMS) return;
    setSelectedRows(prev =>
      prev.find(r => r.id === row.id) ? prev.filter(r => r.id !== row.id) : [...prev, row],
    );
  };

  const toggleAll = () => {
    if (isUpdateInAMS) return;
    setSelectedRows(isAllSelected ? [] : [...llmResponse]);
  };

  const formatDate = (iso: string) => {
    try {
      const utcDate = moment.utc(iso);
      const localDate = moment(utcDate).local();
      return localDate.format('MM/DD/YYYY HH:mm');
    } catch { return iso; }
  };

  const skeletonRows = Array.from({ length: PAGE_SIZE }, (_, i) => i);

  return (
    <>
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14, flexShrink: 0 }}>
        <div className="cs-client-search__input-wrap" style={{ flex: 1 }}>
          <FiSearch size={14} className="cs-client-search__icon" />
          <input
            className="cs-client-search__input"
            placeholder="Search by file name or user…"
            value={globalFilter}
            onChange={e => setGlobalFilter(e.target.value)}
          />
        </div>
        <button
          onClick={fetchHistory}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px',
            background: 'var(--surface-glass)', border: '1px solid var(--border-glass)',
            borderRadius: 8, color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          <FiRefreshCw size={13} /> Refresh
        </button>
      </div>

      {/* Table */}
      <div style={{ flex: 1, overflow: 'auto', borderRadius: 12, border: '1px solid var(--border-glass)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--surface-glass)', borderBottom: '1px solid var(--border-glass)' }}>
              {['File Name', 'Submitted Date', 'User', 'Status', 'Actions', 'Feedback'].map(col => (
                <th key={col} style={{
                  padding: '10px 14px', textAlign: 'left', fontWeight: 600,
                  color: 'var(--text-secondary)', fontSize: 11, letterSpacing: '0.04em',
                  textTransform: 'uppercase', whiteSpace: 'nowrap',
                }}>
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading
              ? skeletonRows.map(i => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border-glass)' }}>
                    {[1, 2, 3, 4, 5, 6].map(j => (
                      <td key={j} style={{ padding: '10px 14px' }}>
                        <Skeleton width="80%" height="1rem" />
                      </td>
                    ))}
                  </tr>
                ))
              : filteredData.length === 0
                ? (
                  <tr>
                    <td colSpan={6} style={{ padding: '32px', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>
                      No history found.
                    </td>
                  </tr>
                )
                : filteredData.map((row, idx) => {
                    const s = STATUS_CONFIG[row.status] || STATUS_CONFIG[0];
                    return (
                      <motion.tr
                        key={row.transactionId}
                        initial={{ opacity: 0, x: -4 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.03, duration: 0.2 }}
                        style={{ borderBottom: '1px solid var(--border-glass)' }}
                      >
                        <td style={{ padding: '10px 14px', color: 'var(--text-primary)', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <span title={row.fileName}>{row.fileName}</span>
                        </td>
                        <td style={{ padding: '10px 14px', color: 'var(--text-secondary)', whiteSpace: 'nowrap', fontSize: 12 }}>
                          {formatDate(row.created)}
                        </td>
                        <td style={{ padding: '10px 14px', color: 'var(--text-secondary)' }}>{row.userName}</td>
                        <td style={{ padding: '10px 14px' }}>
                          {s && <span className="qp-status-pill" style={{ color: s.color }}>{s.label}</span>}
                        </td>
                        <td style={{ padding: '10px 14px' }}>
                          {row.status === 2 && (
                            <button
                              onClick={() => onPreviewClick(row)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                              title="Preview"
                            >
                              <img src={PreviewIcon} alt="Preview" width={30} height={30} />
                            </button>
                          )}
                        </td>
                        <td style={{ padding: '10px 14px' }}>
                          <button
                            onClick={e => openFeedback(row, e)}
                            title="Feedback"
                            style={{
                              background: 'none', border: 'none',
                              cursor: 'pointer', padding: '4px 8px',
                              display: 'inline-flex', alignItems: 'center', gap: 5,
                              color: 'var(--text-secondary)',
                              fontSize: 12,
                            }}
                          >
                            <FiMessageSquare size={13} />
                          </button>
                        </td>
                      </motion.tr>
                    );
                  })
            }
          </tbody>
        </table>
      </div>

      {/* Paginator */}
      {!loading && totalRecords > PAGE_SIZE && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, flexShrink: 0, fontSize: 12, color: 'var(--text-secondary)' }}>
          <span>
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalRecords)} of {totalRecords}
          </span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
              style={{ background: 'var(--surface-glass)', border: '1px solid var(--border-glass)', borderRadius: 6, padding: '4px 10px', cursor: page === 1 ? 'default' : 'pointer', opacity: page === 1 ? 0.4 : 1, color: 'var(--text-secondary)' }}
            >
              <FiChevronLeft size={13} />
            </button>
            <span style={{ padding: '4px 10px' }}>{page} / {totalPages}</span>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}
              style={{ background: 'var(--surface-glass)', border: '1px solid var(--border-glass)', borderRadius: 6, padding: '4px 10px', cursor: page >= totalPages ? 'default' : 'pointer', opacity: page >= totalPages ? 0.4 : 1, color: 'var(--text-secondary)' }}
            >
              <FiChevronRight size={13} />
            </button>
          </div>
        </div>
      )}

      {/* ── Preview Dialog ── */}
      {showPopup && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.65)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
        }}>
          <div style={{
            width: '88vw', height: '90vh', background: 'var(--bg-primary)',
            border: '1px solid var(--border-glass)', borderRadius: 14,
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
            boxShadow: '0 24px 80px rgba(0,0,0,0.5)',
          }}>
            {/* Dialog header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '12px 20px', borderBottom: '1px solid var(--border-glass)',
              background: 'var(--surface-glass)', flexShrink: 0,
            }}>
              <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>
                Loss Run Scan — Preview
              </span>
              <button
                onClick={() => setShowPopup(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}
              >
                <FiX size={18} />
              </button>
            </div>

            {previewLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
                <span className="cs-upload-spinner" style={{ width: 32, height: 32 }} />
              </div>
            ) : (
              <div style={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden' }}>

                {/* Left: PDF */}
                {isFilePdf && (
                  <div style={{ width: '45%', flexShrink: 0, display: 'flex', flexDirection: 'column', borderRight: '1px solid var(--border-glass)' }}>
                    <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border-glass)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Uploaded PDF</span>
                      <motion.button
                        className="cs-btn-start"
                        onClick={() => {
                          setShowUploadDialog(true);
                          setUploadClientName('');
                          setUploadClientSearch('');
                          setUploadClientList([]);
                          setUploadInsuredId('');
                        }}
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.97 }}
                        style={{ fontSize: 12, padding: '6px 14px' }}
                      >
                        Upload in AMS
                      </motion.button>
                    </div>
                    <div style={{ flex: 1, minHeight: 0 }}>
                      <iframe src={pdfUrl} title="Loss Run PDF" style={{ width: '100%', height: '100%', border: 'none' }} />
                    </div>
                  </div>
                )}

                {/* Right: Extracted data */}
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                  {/* Client selector */}
                  <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-glass)', flexShrink: 0 }}>
                    <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>
                      Select Client <span style={{ color: '#ef4444' }}>*</span>
                    </p>
                    <div style={{ position: 'relative' }}>
                      <div className="cs-client-search__input-wrap">
                        <FiSearch size={13} className="cs-client-search__icon" />
                        <input
                          className="cs-client-search__input"
                          placeholder={insuredClientName || 'Enter client name…'}
                          value={insuredSearch}
                          disabled={isUpdateInAMS}
                          onChange={e => { setInsuredSearch(e.target.value); setClientNameError(''); }}
                        />
                        {clientLoading && <span className="cs-client-search__spinner" />}
                      </div>
                      {insuredSearch.trim() && (
                        <div style={{
                          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20,
                          background: 'var(--bg-primary)', border: '1px solid var(--border-glass)',
                          borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.3)', maxHeight: 200, overflowY: 'auto',
                        }}>
                          {clientLoading ? (
                            <div style={{ padding: 12, display: 'flex', justifyContent: 'center' }}>
                              <span className="cs-client-search__spinner" />
                            </div>
                          ) : clientResults.length === 0 ? (
                            <div style={{ padding: '10px 14px', color: 'var(--text-tertiary)', fontSize: 12 }}>No results</div>
                          ) : (
                            clientResults.map(c => (
                              <div
                                key={c.databaseId}
                                style={{ padding: '9px 14px', cursor: 'pointer', fontSize: 12, color: 'var(--text-primary)', borderBottom: '1px solid var(--border-glass)' }}
                                onClick={() => {
                                  setInsuredSearch('');
                                  setInsuredClientName(c.name);
                                  setClientName(c.name);
                                  setClientId(c.databaseId);
                                  setClientResults([]);
                                  setClientNameError('');
                                }}
                              >
                                {c.name}
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                    {clientNameError && <p style={{ color: '#ef4444', fontSize: 11, marginTop: 4 }}>{clientNameError}</p>}
                  </div>

                  {/* Claims table */}
                  <div style={{ flex: 1, overflow: 'auto', padding: '12px 16px' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>
                      Extracted Data {llmResponse.length > 0 && `(${llmResponse.length} claims)`}
                    </div>
                    {llmResponse.length === 0 ? (
                      <div style={{ color: 'var(--text-tertiary)', fontSize: 13, padding: '24px 0', textAlign: 'center' }}>
                        No claims extracted.
                      </div>
                    ) : (
                      <>
                        <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid var(--border-glass)' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                            <thead>
                              <tr style={{ background: 'var(--surface-glass)', borderBottom: '1px solid var(--border-glass)' }}>
                                <th style={{ padding: '8px 10px', textAlign: 'center', width: 36 }}>
                                  <input
                                    type="checkbox"
                                    checked={isAllSelected}
                                    disabled={isUpdateInAMS}
                                    onChange={toggleAll}
                                    style={{ cursor: isUpdateInAMS ? 'not-allowed' : 'pointer' }}
                                  />
                                </th>
                                {['Claim #', 'Year', 'Carrier', 'Policy #', 'LOB', 'Premium', 'Loss Date', 'Incurred', 'Paid', 'Outstanding'].map(col => (
                                  <th key={col} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 11, whiteSpace: 'nowrap', letterSpacing: '0.03em' }}>
                                    {col}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {claimsSlice.map((row, i) => {
                                const isSelected = !!selectedRows.find(r => r.id === row.id);
                                return (
                                  <tr
                                    key={row.id}
                                    onClick={() => toggleRow(row)}
                                    style={{
                                      borderBottom: '1px solid var(--border-glass)',
                                      background: isSelected ? 'rgba(124,58,237,0.08)' : i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)',
                                      cursor: isUpdateInAMS ? 'default' : 'pointer',
                                    }}
                                  >
                                    <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                                      <input
                                        type="checkbox"
                                        checked={isSelected}
                                        disabled={isUpdateInAMS}
                                        onChange={() => toggleRow(row)}
                                        style={{ cursor: isUpdateInAMS ? 'not-allowed' : 'pointer' }}
                                        onClick={e => e.stopPropagation()}
                                      />
                                    </td>
                                    {[row.ClaimNumber, row.Year, row.Carrier, row.PolicyNumber, row.LOB, row.Premium, row.LossDate, row.Incurred, row.Paid, row.Outstanding].map((val, ci) => (
                                      <td key={ci} style={{ padding: '8px 10px', color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>
                                        {val || '—'}
                                      </td>
                                    ))}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>

                        {/* Claims pagination */}
                        {claimsPages > 1 && (
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 8 }}>
                            <button disabled={claimsPage === 1} onClick={() => setClaimsPage(p => p - 1)}
                              style={{ background: 'var(--surface-glass)', border: '1px solid var(--border-glass)', borderRadius: 6, padding: '3px 10px', cursor: 'pointer', color: 'var(--text-secondary)', opacity: claimsPage === 1 ? 0.4 : 1 }}>
                              <FiChevronLeft size={12} />
                            </button>
                            <span style={{ fontSize: 11, color: 'var(--text-secondary)', padding: '4px 6px' }}>{claimsPage}/{claimsPages}</span>
                            <button disabled={claimsPage >= claimsPages} onClick={() => setClaimsPage(p => p + 1)}
                              style={{ background: 'var(--surface-glass)', border: '1px solid var(--border-glass)', borderRadius: 6, padding: '3px 10px', cursor: 'pointer', color: 'var(--text-secondary)', opacity: claimsPage >= claimsPages ? 0.4 : 1 }}>
                              <FiChevronRight size={12} />
                            </button>
                          </div>
                        )}

                        {/* Insert button */}
                        {!isUpdateInAMS && (
                          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: 12 }}>
                            {claimInserted ? (
                              <div style={{
                                display: 'flex', alignItems: 'center', gap: 8,
                                background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.25)',
                                borderRadius: 8, padding: '8px 16px',
                                color: '#22c55e', fontSize: 13, fontWeight: 600,
                              }}>
                                <FiCheck size={15} />
                                Claim data inserted successfully!
                              </div>
                            ) : (
                              <motion.button
                                className="cs-btn-start"
                                disabled={insertLoading || selectedRows.length === 0}
                                whileHover={{ scale: 1.03 }}
                                whileTap={{ scale: 0.97 }}
                                onClick={insertClaimData}
                                style={{ fontSize: 12, padding: '8px 22px' }}
                              >
                                {insertLoading ? <span className="cs-upload-spinner" /> : (
                                  <><FiCheck size={13} style={{ marginRight: 6 }} />Insert Claim</>
                                )}
                              </motion.button>
                            )}
                          </div>
                        )}

                        {isUpdateInAMS && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, fontSize: 12, color: '#22c55e', fontWeight: 600 }}>
                            <FiCheck size={13} /> Already updated in AMS
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Upload in AMS Dialog ── */}
      {showUploadDialog && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(0,0,0,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
        }}>
          <div style={{
            width: 400, background: 'var(--bg-primary)', border: '1px solid var(--border-glass)',
            borderRadius: 12, padding: '24px', boxShadow: '0 16px 60px rgba(0,0,0,0.5)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
              <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>Upload in AMS</span>
              <button onClick={() => setShowUploadDialog(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}>
                <FiX size={16} />
              </button>
            </div>

            <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>Search Client</p>
            <div style={{ position: 'relative' }}>
              <div className="cs-client-search__input-wrap">
                <FiSearch size={13} className="cs-client-search__icon" />
                <input
                  className="cs-client-search__input"
                  placeholder={uploadClientName || 'Enter client name…'}
                  value={uploadClientSearch}
                  onChange={e => {
                    setUploadClientSearch(e.target.value);
                    setUploadClientName(e.target.value);
                    if (!e.target.value) { setUploadInsuredId(''); setUploadClientList([]); }
                  }}
                />
                {uploadClientLoading && <span className="cs-client-search__spinner" />}
              </div>
              {uploadClientSearch && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20,
                  background: 'var(--bg-primary)', border: '1px solid var(--border-glass)',
                  borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.3)', maxHeight: 200, overflowY: 'auto',
                }}>
                  {uploadClientLoading ? (
                    <div style={{ padding: 12, display: 'flex', justifyContent: 'center' }}><span className="cs-client-search__spinner" /></div>
                  ) : uploadClientList.length === 0 ? (
                    <div style={{ padding: '10px 14px', color: 'var(--text-tertiary)', fontSize: 12 }}>No results found</div>
                  ) : (
                    uploadClientList.map(c => (
                      <div
                        key={c.databaseId}
                        style={{ padding: '9px 14px', cursor: 'pointer', fontSize: 12, color: 'var(--text-primary)', borderBottom: '1px solid var(--border-glass)' }}
                        onClick={() => {
                          setUploadInsuredId(c.databaseId);
                          setUploadClientName(c.name);
                          setUploadClientSearch('');
                          setUploadClientList([]);
                        }}
                      >
                        {c.name}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
              <button className="cs-btn-cancel" onClick={() => setShowUploadDialog(false)}>Cancel</button>
              {uploadToAmsLoading ? (
                <span className="cs-upload-spinner" />
              ) : (
                <motion.button
                  className="cs-btn-start"
                  disabled={!uploadInsuredId}
                  whileHover={{ scale: uploadInsuredId ? 1.03 : 1 }}
                  onClick={onUploadToAMS}
                  style={{ fontSize: 12, padding: '8px 20px' }}
                >
                  Upload
                </motion.button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>

    {/* ── Feedback Popup ── */}
    {feedbackTxId && feedbackAnchor && (
      <div
        ref={feedbackRef}
        className="csfb-popup"
        style={{ top: feedbackAnchor.top, right: feedbackAnchor.right }}
      >
        <h4 className="csfb-title">Feedback</h4>
        <textarea
          className="csfb-textarea"
          placeholder="Enter your feedback here"
          value={feedbackText}
          onChange={e => setFeedbackText(e.target.value)}
          rows={4}
        />
        <div className="csfb-rating">
          <button
            className={`csfb-rating-btn${feedbackRating === true ? ' csfb-rating-btn--up' : ''}`}
            onClick={onThumbsUpClick}
            title="Thumbs up"
          >
            <FiThumbsUp size={20} />
          </button>
          <button
            className={`csfb-rating-btn${feedbackRating === false ? ' csfb-rating-btn--down' : ''}`}
            onClick={onThumbsDownClick}
            title="Thumbs down"
          >
            <FiThumbsDown size={20} />
          </button>
        </div>
        {feedBackError && <p className="csfb-error">{feedBackError}</p>}
        <div className="csfb-actions">
          <button className="csfb-btn-cancel" onClick={closeFeedback}>Cancel</button>
          <button
            className="csfb-btn-submit"
            disabled={feedbackSubmitting}
            onClick={onSubmitFeedbackClick}
          >
            {feedbackSubmitting ? 'Submitting…' : 'Submit'}
          </button>
        </div>
      </div>
    )}
    </>
  );
};

export default LossRunScanHistory;

